import Base from '../base/base';
import { LEADS_API } from '../../shared/utils/variables';
import './lead-capture-modal.scss';

interface LandAddress {
  formatted_address: string;
  location: {
    lat: number;
    lng: number;
  };
}

interface LeadCaptureModalOptions {
  triggerSelector?: string;
  useLocalApi?: boolean;
  userType?: string;
  debug?: boolean;
  [key: string]: any;
}

interface LeadCaptureCopy {
  title: string;
  subtitle: string;
  successTitle: string;
  successText: string;
}

const LEAD_CAPTURE_COPY: Record<string, LeadCaptureCopy> = {
  'newsletter': {
    title: 'Join Our Newsletter',
    subtitle: 'Get updates on new homes and upcoming projects.',
    successTitle: 'Thanks for joining!',
    successText: 'You are on the list for updates on new homes and projects.'
  },
  'interest-list': {
    title: 'Join the Interest List',
    subtitle: 'Get updates as this plan becomes available.',
    successTitle: 'Thanks for your interest!',
    successText: 'You are on the list for updates on this plan.'
  }
};

export default class LeadCaptureModal extends Base {
  private triggerSelector: string;
  private useLocalApi: boolean;
  private userType: string;
  private modal: HTMLElement | null = null;
  private backdrop: HTMLElement | null = null;
  private dialog: HTMLElement | null = null;
  private form: HTMLFormElement | null = null;
  private status: HTMLElement | null = null;
  private addressLine: HTMLElement | null = null;
  private successPanel: HTMLElement | null = null;
  private title: HTMLElement | null = null;
  private subtitle: HTMLElement | null = null;
  private successTitle: HTMLElement | null = null;
  private successText: HTMLElement | null = null;
  private successCloseButton: HTMLButtonElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private lastFocusedElement: HTMLElement | null = null;
  private onKeyDown: ((event: KeyboardEvent) => void) | null = null;
  private currentTrigger: HTMLElement | null = null;
  private scrollLockY: number | null = null;

  constructor(options: LeadCaptureModalOptions = {}) {
    super(options.debug || false);
    this.triggerSelector = options.triggerSelector || '#join-newsletter';
    this.useLocalApi = options.useLocalApi || false;
    this.userType = options.userType || 'newsletter';

    this.init();
  }

  init(): void {
    const triggers = Array.from(document.querySelectorAll<HTMLElement>(this.triggerSelector));
    if (!triggers.length) {
      this.log('LeadCaptureModal: triggers not found');
      return;
    }

    this.createModal();

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      });
    });

    this.log('LeadCaptureModal initialized');
  }

  private createModal(): void {
    const modal = document.createElement('div');
    modal.className = 'lead-capture-modal';
    modal.setAttribute('aria-hidden', 'true');

    modal.innerHTML = `
      <div class="lead-capture-modal__backdrop" data-role="backdrop"></div>
      <div class="lead-capture-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="lead-capture-title">
        <button type="button" class="lead-capture-modal__close" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
        <div class="lead-capture-modal__content">
          <h2 id="lead-capture-title" class="lead-capture-modal__title">Join Our Newsletter</h2>
          <p class="lead-capture-modal__subtitle" data-role="subtitle">Get updates on new homes and upcoming projects.</p>
          <p class="lead-capture-modal__address is-hidden" data-role="address"></p>
          <form class="lead-capture-modal__form" novalidate>
            <div class="lead-capture-modal__field-row">
              <div class="lead-capture-modal__field">
                <label class="lead-capture-modal__label" for="lead-capture-first-name">First Name</label>
                <input id="lead-capture-first-name" name="firstName" type="text" class="lead-capture-modal__input" required />
              </div>
              <div class="lead-capture-modal__field">
                <label class="lead-capture-modal__label" for="lead-capture-last-name">Last Name</label>
                <input id="lead-capture-last-name" name="lastName" type="text" class="lead-capture-modal__input" required />
              </div>
            </div>
            <div class="lead-capture-modal__field">
              <label class="lead-capture-modal__label" for="lead-capture-email">Email</label>
              <input id="lead-capture-email" name="email" type="email" class="lead-capture-modal__input" required />
            </div>
            <div class="lead-capture-modal__field">
              <label class="lead-capture-modal__label" for="lead-capture-phone">Phone (optional)</label>
              <input id="lead-capture-phone" name="phone" type="tel" class="lead-capture-modal__input" />
            </div>
            <button type="submit" class="lead-capture-modal__submit">Sign Up</button>
            <p class="lead-capture-modal__status" role="status" aria-live="polite"></p>
          </form>
          <div class="lead-capture-modal__success" role="status" aria-live="polite">
            <h3 class="lead-capture-modal__success-title" data-role="success-title">Thanks for joining!</h3>
            <p class="lead-capture-modal__success-text" data-role="success-text">You are on the list for updates on new homes and projects.</p>
            <button type="button" class="lead-capture-modal__close-cta">Close</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    this.modal = modal;
    this.backdrop = modal.querySelector<HTMLElement>('[data-role="backdrop"]');
    this.dialog = modal.querySelector<HTMLElement>('.lead-capture-modal__dialog');
    this.form = modal.querySelector<HTMLFormElement>('.lead-capture-modal__form');
    this.status = modal.querySelector<HTMLElement>('.lead-capture-modal__status');
    this.addressLine = modal.querySelector<HTMLElement>('[data-role="address"]');
    this.successPanel = modal.querySelector<HTMLElement>('.lead-capture-modal__success');
    this.title = modal.querySelector<HTMLElement>('.lead-capture-modal__title');
    this.subtitle = modal.querySelector<HTMLElement>('[data-role="subtitle"]');
    this.successTitle = modal.querySelector<HTMLElement>('[data-role="success-title"]');
    this.successText = modal.querySelector<HTMLElement>('[data-role="success-text"]');
    this.successCloseButton = modal.querySelector<HTMLButtonElement>('.lead-capture-modal__close-cta');
    this.closeButton = modal.querySelector<HTMLButtonElement>('.lead-capture-modal__close');

    this.bindModalEvents();
  }

  private bindModalEvents(): void {
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleSubmit();
      });
    }

    if (this.successCloseButton) {
      this.successCloseButton.addEventListener('click', () => this.close());
    }
  }

  private open(trigger: HTMLElement): void {
    if (!this.modal) return;

    this.currentTrigger = trigger;
    this.lastFocusedElement = document.activeElement as HTMLElement | null;
    this.resetState();
    this.applyCopy();
    this.lockScroll();
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');

    this.onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };

    document.addEventListener('keydown', this.onKeyDown);

    const firstInput = this.modal.querySelector<HTMLElement>('.lead-capture-modal__input');
    firstInput?.focus();
  }

  private close(): void {
    if (!this.modal) return;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.unlockScroll();

    if (this.onKeyDown) {
      document.removeEventListener('keydown', this.onKeyDown);
      this.onKeyDown = null;
    }

    this.lastFocusedElement?.focus();
  }

  private lockScroll(): void {
    if (this.scrollLockY !== null) return;

    this.scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('lead-capture-modal--open');
    document.body.style.top = `-${this.scrollLockY}px`;
  }

  private unlockScroll(): void {
    if (this.scrollLockY === null) return;

    document.body.classList.remove('lead-capture-modal--open');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollLockY);
    this.scrollLockY = null;
  }

  private async handleSubmit(): Promise<void> {
    if (!this.form || !this.status) return;

    const formData = new FormData(this.form);
    const firstName = (formData.get('firstName') || '').toString().trim();
    const lastName = (formData.get('lastName') || '').toString().trim();
    const email = (formData.get('email') || '').toString().trim();
    const phone = (formData.get('phone') || '').toString().trim();

    if (!firstName || !lastName || !email) {
      this.status.textContent = 'Please complete all required fields.';
      this.status.classList.add('is-error');
      return;
    }

    this.status.textContent = 'Submitting...';
    this.status.classList.remove('is-error');
    this.status.classList.add('is-loading');

    const payload: Record<string, unknown> = {
      firstName,
      lastName,
      email,
      userType: this.userType,
      urlPath: window.location.pathname
    };

    if (phone) {
      payload.phone = phone;
    }

    if (this.userType === 'interest-list') {
      const parent = this.getInterestParent(this.currentTrigger);
      const interestType = this.getInterestType(parent);

      if (interestType === 'Coming Soon') {
        payload.note = 'Coming Soon';
      } else {
        const landAddress = this.buildLandAddress(this.currentTrigger, parent);
        if (!landAddress) {
          this.status.textContent = 'Please contact website admin. Error code: NO_LATLONG';
          this.status.classList.remove('is-loading');
          this.status.classList.add('is-error');
          return;
        }

        payload.landAddress = landAddress;
      }
    }

    try {
      const response = await fetch(this.getEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.status.textContent = '';
      this.status.classList.remove('is-loading');
      this.modal?.classList.add('is-success');
      this.form.reset();
      this.successCloseButton?.focus();
    } catch (error) {
      this.log('LeadCaptureModal submit error', error);
      this.status.textContent = 'Something went wrong. Please try again.';
      this.status.classList.remove('is-loading');
      this.status.classList.add('is-error');
    }
  }

  private resetState(): void {
    if (!this.modal) return;

    this.modal.classList.remove('is-success');

    if (this.status) {
      this.status.textContent = '';
      this.status.classList.remove('is-error', 'is-loading');
    }

    const interestLabel = this.resolveInterestLabel(this.currentTrigger);
    this.updateAddressLine(interestLabel);
  }

  private getCopyForUserType(): LeadCaptureCopy {
    return LEAD_CAPTURE_COPY[this.userType] || LEAD_CAPTURE_COPY.newsletter;
  }

  private applyCopy(): void {
    const copy = this.getCopyForUserType();

    if (this.title) this.title.textContent = copy.title;
    if (this.subtitle) this.subtitle.textContent = copy.subtitle;
    if (this.successTitle) this.successTitle.textContent = copy.successTitle;
    if (this.successText) this.successText.textContent = copy.successText;
  }

  private getInterestParent(trigger: HTMLElement | null): HTMLElement | null {
    if (!trigger) return null;
    return trigger.closest('.hc-actions');
  }

  private getInterestType(parent: HTMLElement | null): string {
    if (!parent) return '';
    return (parent.dataset.interestType || '').trim();
  }

  private buildLandAddress(trigger: HTMLElement | null, parent: HTMLElement | null): LandAddress | null {
    if (!trigger || !parent) return null;

    const propertyId = (trigger.dataset.propertyId || '').trim();
    const latValue = parent.dataset.lat || '';
    const lngValue = parent.dataset.long || '';
    const lat = Number.parseFloat(latValue);
    const lng = Number.parseFloat(lngValue);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return {
      formatted_address: propertyId,
      location: { lat, lng }
    };
  }

  private resolveInterestLabel(trigger: HTMLElement | null): string | null {
    if (this.userType !== 'interest-list' || !trigger) return null;

    const parent = this.getInterestParent(trigger);
    const interestValue = this.getInterestType(parent) === 'Coming Soon'
      ? 'Coming Soon'
      : (trigger.dataset.propertyId || '').trim();

    return interestValue ? `Interested In: ${interestValue}` : null;
  }

  private updateAddressLine(label: string | null): void {
    if (!this.addressLine) return;

    if (!label) {
      this.addressLine.textContent = '';
      this.addressLine.classList.add('is-hidden');
      return;
    }

    this.addressLine.textContent = label;
    this.addressLine.classList.remove('is-hidden');
  }

  private getEndpoint(): string {
    if (this.useLocalApi) {
      return 'http://localhost:3000/api/leads';
    }

    return LEADS_API;
  }
}
